import { GitPullRequestIcon, CpuIcon, RocketIcon, GearIcon, HistoryIcon, HomeIcon, KeyIcon, LockIcon, OrganizationIcon, PeopleIcon, PlusIcon, RepoIcon, SearchIcon, ServerIcon } from "@primer/octicons-react";
import { rp } from "../api/client";
import { useState } from "react";
import { Link, Navigate, useLocation, useParams, useSearchParams } from "react-router-dom";
import { ScopeProvider } from "../lib/scope";
import { useAction, useMe, useOrg, useRepos } from "../api/hooks";
import { openCount, type OrgDetail } from "../api/types";
import { AvatarImage } from "../components/AvatarImage";
import { Empty, ErrorMessage, Loading, Page, Query, TimeAgo } from "../components/common";
import { SideNav, SideNavLayout, Subhead, sideNavItem } from "../components/SettingsLayout";
import { type NavTab, SubNav } from "../components/SubNav";
import { Badge, Counter } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { ConfirmDialog } from "../components/ui/confirm-dialog";
import { Input } from "../components/ui/input";
import { Select } from "../components/ui/select";
import { AuditLog } from "./Audit";
import { AdminQueue, AdminTokens } from "./Admin";
import { AdminApps } from "./AdminApps";
import { AdminRunners } from "./AdminRunners";
import { RepoList, type RepoWithInfo } from "./Explore";

const updated = (r: RepoWithInfo) => r.info?.last?.ts ?? 0;

/** `/`: your personal organization (named after you), or the first one you belong to. */
export function Home() {
  const me = useMe();
  if (me.isLoading) return <Page><Loading /></Page>;
  if (me.isError) return <Page><ErrorMessage error={me.error} /></Page>;
  const orgs = me.data?.orgs ?? [];
  const mine = orgs.find((o) => o.name === me.data?.user?.name.toLowerCase()) ?? orgs[0];
  if (mine) return <Navigate to={`/orgs/${encodeURIComponent(mine.name)}`} replace />;
  return (
    <Page>
      <Empty>
        You are not in an organization yet. <Link to="/orgs/new" className="text-accent-fg hover:underline">Create one</Link> to host repositories.
      </Empty>
    </Page>
  );
}

/** `/orgs/:name`: an organization, its repositories and its settings. */
export function OrgPage() {
  const { name = "" } = useParams();
  const q = useOrg(name);
  if (q.isLoading) return <Page><Loading /></Page>;
  if (!q.data) return <Page><ErrorMessage error={q.error} /></Page>;
  return <Owner org={q.data} />;
}

type Tab = "overview" | "repositories" | "settings";
type Section = "members" | "tokens" | "queue" | "runners" | "webhooks" | "audit" | "general";

function Owner({ org }: { org: OrgDetail }) {
  const name = org.name;
  const me = useMe();
  const repos = useRepos();
  const [params] = useSearchParams();
  const mine = org.members.find((m) => m.name === me.data?.user?.name);
  const isOwner = mine?.role === "owner";
  const admin = !!me.data?.actor.actions.includes("admin");
  const canCreate = !!mine || admin;
  const list = (repos.data ?? []).filter((r) => r.owner === name);
  const open = list.reduce((n, r) => n + (r.info ? openCount(r.info.counts) : 0), 0);
  const { "*": section = "" } = useParams();
  const settings = useLocation().pathname.includes("/settings");
  const asked = params.get("tab");
  const tab: Tab = settings && (mine || admin) ? "settings" : asked === "repositories" ? "repositories" : "overview";
  const base = `/orgs/${encodeURIComponent(name)}`;
  const items = settingsItems(isOwner);
  const area = items.find((i) => i.id === section)?.id ?? "members";
  const tabs: NavTab[] = [
    { to: base, Icon: HomeIcon, label: "Overview", current: tab === "overview" },
    { to: `${base}?tab=repositories`, Icon: RepoIcon, label: "Repositories", count: list.length, current: tab === "repositories" },
    ...(mine || admin ? [{ to: `${base}/settings/members`, Icon: GearIcon, label: "Settings", current: tab === "settings" }] : []),
  ];
  return (
    <>
      <SubNav label="Organization" tabs={tabs} />
      <Page>
        {tab !== "settings" && (
          <div className="mt-6 flex flex-wrap items-center gap-4 border-b border-border-muted pb-6">
            <span className="grid size-16 shrink-0 place-items-center overflow-hidden rounded-md border bg-muted">
              <OrganizationIcon size={36} />
            </span>
            <div className="min-w-0 flex-1">
              <h1 className="truncate text-2xl font-semibold">{name}</h1>
              <p className="mt-1 flex flex-wrap gap-x-4 text-sm text-muted-foreground [&>span]:inline-flex [&>span]:items-center [&>span]:gap-1">
                {org.description ? <span>{org.description}</span> : null}
                <span>
                  <RepoIcon size={14} /> {repos.data ? list.length : "–"} {list.length === 1 ? "repository" : "repositories"}
                </span>
                <span>
                  <GitPullRequestIcon size={14} /> {repos.data ? open : "–"} open {open === 1 ? "change" : "changes"}
                </span>
                <span>
                  <PeopleIcon size={14} /> {org.members.length} {org.members.length === 1 ? "person" : "people"}
                </span>
              </p>
            </div>
            {canCreate && <NewRepoButton owner={name} />}
          </div>
        )}
        {tab === "overview" && <Overview repos={repos} list={list} base={base} />}
        {tab === "repositories" && <Repositories repos={repos} list={list} owner={name} />}
        {tab === "settings" && (
          <SideNavLayout
            nav={
              <SideNav label="Organization settings">
                {items.map((i) => {
                  const count = i.count?.(org);
                  return (
                    <Link key={i.id} to={`${base}/settings/${i.id}`} className={sideNavItem} aria-current={i.id === area ? "page" : undefined}>
                      <i.Icon size={16} /> <span className="min-w-0 flex-1">{i.label}</span> {count ? <Counter>{count}</Counter> : null}
                    </Link>
                  );
                })}
              </SideNav>
            }
          >
            <OrgSettings org={org} isOwner={isOwner} selfName={me.data?.user?.name ?? ""} section={area} />
          </SideNavLayout>
        )}
      </Page>
    </>
  );
}

function NewRepoButton({ owner }: { owner: string }) {
  return (
    <Button asChild variant="primary">
      <Link to={`/new?owner=${encodeURIComponent(owner)}`}>
        <RepoIcon /> New repository
      </Link>
    </Button>
  );
}

function EmptyRepos({ owner }: { owner: string }) {
  return (
    <Card className="mt-0">
      <Empty icon={RepoIcon} bee="idle">
        <div>{owner} has no repositories yet.</div>
      </Empty>
    </Card>
  );
}

function Overview({ repos, list, base }: { repos: ReturnType<typeof useRepos>; list: RepoWithInfo[]; base: string }) {
  return (
    <section className="mt-6">
      <div className="mb-2 flex items-center">
        <h2 className="flex-1 text-base font-semibold">Recently updated repositories</h2>
        <Link className="text-xs" to={base === "/" ? "/?tab=repositories" : `${base}?tab=repositories`}>
          View all
        </Link>
      </div>
      <Query q={repos}>
        {() =>
          list.length ? (
            <div className="grid grid-cols-3 gap-4 max-lg:grid-cols-2 max-md:grid-cols-1">
              {[...list]
                .sort((a, b) => updated(b) - updated(a))
                .slice(0, 6)
                .map((r) => (
                  <RepoCard key={r.name} r={r} />
                ))}
            </div>
          ) : (
            <EmptyRepos owner="Nobody" />
          )
        }
      </Query>
    </section>
  );
}

function RepoCard({ r }: { r: RepoWithInfo }) {
  const open = r.info ? openCount(r.info.counts) : 0;
  return (
    <div className="flex min-w-0 flex-col rounded-md border p-4">
      <div className="flex items-center gap-2">
        <RepoIcon size={16} className="shrink-0 text-muted-foreground" />
        <Link className="truncate font-semibold" to={rp(r.slug, r.owner)}>
          {r.slug}
        </Link>
        <Badge>{r.private ? "Private" : "Public"}</Badge>
        {r.info?.frozen && <Badge tone="red">Frozen</Badge>}
      </div>
      <p className="mt-1 line-clamp-2 min-h-10 flex-1 text-xs text-muted-foreground">{r.description || "No description"}</p>
      <div className="mt-2 flex flex-wrap gap-x-4 text-xs text-muted-foreground [&>span]:inline-flex [&>span]:items-center [&>span]:gap-1">
        <span>
          <GitPullRequestIcon size={12} /> {open} open
        </span>
        {r.info?.last ? (
          <span>
            Updated <TimeAgo ms={r.info.last.ts} />
          </span>
        ) : (
          <span>{r.import_state === "pending" ? "importing…" : "empty"}</span>
        )}
      </div>
    </div>
  );
}

const SORTS = [
  { value: "updated", label: "Last updated" },
  { value: "name", label: "Name" },
  { value: "open", label: "Open changes" },
];

function Repositories({ repos, list, owner }: { repos: ReturnType<typeof useRepos>; list: RepoWithInfo[]; owner: string }) {
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("updated");
  const needle = q.trim().toLowerCase();
  const shown = list
    .filter((r) => !needle || r.slug.includes(needle) || r.description?.toLowerCase().includes(needle))
    .sort((a, b) =>
      sort === "name" ? a.name.localeCompare(b.name) : sort === "open" ? (b.info ? openCount(b.info.counts) : 0) - (a.info ? openCount(a.info.counts) : 0) : updated(b) - updated(a),
    );
  return (
    <div className="mt-6">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <label className="relative min-w-60 flex-1">
          <SearchIcon size={16} className="pointer-events-none absolute left-2 top-2 text-muted-foreground" />
          <Input className="pl-8" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a repository…" aria-label="Find a repository" />
        </label>
        <div className="w-44">
          <Select value={sort} onValueChange={setSort} options={SORTS} aria-label="Sort repositories" />
        </div>
      </div>
      <Query q={repos}>
        {() =>
          shown.length ? (
            <RepoList repos={shown} />
          ) : list.length ? (
            <Card className="mt-0">
              <Empty icon={RepoIcon}>
                No repositories match <b>{q}</b>.
              </Empty>
            </Card>
          ) : (
            <EmptyRepos owner={owner} />
          )
        }
      </Query>
    </div>
  );
}

const ROLES = [
  { value: "member", label: "Member" },
  { value: "owner", label: "Owner" },
];

function People({ org, isOwner, selfName }: { org: OrgDetail; isOwner: boolean; selfName: string }) {
  const act = useAction();
  const [user, setUser] = useState("");
  const [role, setRole] = useState("member");
  const set = (u: string, r: string, done?: () => void) =>
    act.mutate({ method: "PUT", path: `/v1/orgs/${encodeURIComponent(org.name)}/members/${encodeURIComponent(u)}`, body: { role: r } }, { onSuccess: () => { done?.(); refresh(); } });
  const { refetch } = useOrg(org.name);
  const me = useMe();
  const refresh = () => { refetch(); me.refetch(); };
  return (
    <div className="mt-6">
      <Subhead title="Members">{isOwner ? "Add people by their username. They must have signed in to bee once." : "People in this organization."}</Subhead>
      {isOwner && (
        <form
          className="mb-4 flex flex-wrap items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (user.trim()) set(user.trim(), role, () => setUser(""));
          }}
        >
          <Input className="min-w-60 flex-1" value={user} onChange={(e) => setUser(e.target.value)} placeholder="Username" aria-label="Username to add" />
          <div className="w-32">
            <Select value={role} onValueChange={setRole} options={ROLES} aria-label="Role" />
          </div>
          <Button variant="primary" disabled={act.isPending || !user.trim()}>
            <PlusIcon /> Add
          </Button>
        </form>
      )}
      {act.error ? <div className="mb-3"><ErrorMessage error={act.error} /></div> : null}
      <ul className="rounded-md border">
        {org.members.map((m) => (
          <li key={m.id} className="flex items-center gap-3 border-t border-border-muted p-3 first:border-t-0">
            <AvatarImage src={m.avatar_url} name={m.name} size={32} />
            <span className="min-w-0 flex-1 truncate">
              <span className="font-semibold">{m.name}</span>
              {m.display && m.display !== m.name && <span className="ml-2 text-muted-foreground">{m.display}</span>}
            </span>
            {isOwner ? (
              <>
                <div className="w-32">
                  <Select value={m.role} onValueChange={(r) => set(m.name, r)} options={ROLES} aria-label={`Role of ${m.name}`} />
                </div>
                <ConfirmDialog
                  title={m.name === selfName ? "Leave organization" : "Remove member"}
                  description={`${m.name === selfName ? "You" : m.name} will lose access to ${org.name}'s private repositories.`}
                  confirmLabel="Remove"
                  onConfirm={() => act.mutate({ method: "DELETE", path: `/v1/orgs/${encodeURIComponent(org.name)}/members/${encodeURIComponent(m.name)}`, body: undefined }, { onSuccess: refresh })}
                >
                  <Button size="sm" variant="danger">
                    {m.name === selfName ? "Leave" : "Remove"}
                  </Button>
                </ConfirmDialog>
              </>
            ) : (
              <Badge>{m.role === "owner" ? "Owner" : "Member"}</Badge>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function General({ org }: { org: OrgDetail }) {
  const act = useAction();
  const me = useMe();
  return (
    <div className="mt-6">
      <Subhead title="Danger zone">Deleting an organization removes its membership. Repositories it owns stay on the server but lose their owner.</Subhead>
      <div className="flex items-center gap-3 rounded-md border border-danger p-4">
        <div className="min-w-0 flex-1">
          <div className="font-semibold">Delete this organization</div>
          <p className="text-xs text-muted-foreground">Delete {org.name} and everyone's membership in it.</p>
        </div>
        <ConfirmDialog
          title={`Delete ${org.name}`}
          description="This removes the organization and its memberships. Its repositories are not deleted."
          confirmLabel="Delete organization"
          onConfirm={() =>
            act.mutate({ method: "DELETE", path: `/v1/orgs/${encodeURIComponent(org.name)}`, body: undefined }, { onSuccess: () => { me.refetch().then(() => { window.location.href = "/"; }); } })
          }
        >
          <Button variant="danger">
            <LockIcon /> Delete organization
          </Button>
        </ConfirmDialog>
      </div>
      {act.error ? <div className="mt-3"><ErrorMessage error={act.error} /></div> : null}
    </div>
  );
}

type SettingsItem = { id: Section; Icon: typeof HomeIcon; label: string; count?: (org: OrgDetail) => number };

/** The organization's settings pages, as the Settings section of its menu lists them; owners also see tokens, webhooks, runners and General. */
function settingsItems(isOwner: boolean): SettingsItem[] {
  return [
    { id: "members", Icon: PeopleIcon, label: "Members", count: (o) => o.members.length },
    ...(isOwner ? [{ id: "tokens" as const, Icon: KeyIcon, label: "Tokens" }, { id: "webhooks" as const, Icon: RocketIcon, label: "Webhooks" }] : []),
    { id: "queue", Icon: ServerIcon, label: "Job queue" },
    ...(isOwner ? [{ id: "runners" as const, Icon: CpuIcon, label: "Runners" }] : []),
    { id: "audit", Icon: HistoryIcon, label: "Audit log" },
    ...(isOwner ? [{ id: "general" as const, Icon: GearIcon, label: "General" }] : []),
  ];
}

/** The organization's settings: tokens, webhooks, runners, queue and audit log, each limited to what the organization owns. */
function OrgSettings({ org, isOwner, selfName, section: current }: { org: OrgDetail; isOwner: boolean; selfName: string; section: Section }) {
  return (
    <div>
      <ScopeProvider value={org.name}>
        {current === "members" && <People org={org} isOwner={isOwner} selfName={selfName} />}
        {current === "tokens" && <AdminTokens />}
        {current === "webhooks" && <AdminApps />}
        {current === "runners" && <AdminRunners />}
        {current === "queue" && <AdminQueue />}
        {current === "audit" && (
          <>
            <Subhead title="Audit log">Every mutation and policy decision across this organization's repositories and settings, append-only.</Subhead>
            <Card className="mt-0">
              <AuditLog org={org.name} />
            </Card>
          </>
        )}
        {current === "general" && <General org={org} />}
      </ScopeProvider>
    </div>
  );
}
